<script setup lang="ts">
/**
 * Affichage UNIFIÉ et réutilisable d'un objet de jeu (cf wiki §24.12).
 *
 * Trois tailles :
 *   - `mini`   : carré image seul (+ badge quantité, halo rareté).
 *   - `medium` : rectangle image + nom + rareté + 1-2 stats clés.
 *   - `large`  : carte complète inline (= contenu du tooltip, sans popover).
 *
 * `mini` et `medium` sont enveloppés dans un <AppInfoCard> (popover au survol)
 * qui rend la carte complète `<ItemTooltipContent>` (le mode « grand » vit dans
 * le tooltip — décision produit). Désactivable via `tooltip=false`.
 *
 * Comparaison : avec `comparable`, un bouton ⇄ épingle l'objet comme référence
 * (store itemComparison). Tant qu'un objet est épinglé, le tooltip des AUTRES
 * objets affiche les deltas par rapport à lui (cf ItemTooltipContent).
 *
 * Accepte indifféremment un MeItemEntry, un ExcavationLootItem ou un
 * DisplayItem (normalisé en interne par toDisplayItem). Slot `actions` pour
 * greffer des boutons spécifiques à la vue (ex : « Consommer » de l'inventaire).
 */
import { computed } from 'vue';
import type { Placement } from 'element-plus';
import { thumbUrlFromCode } from '@/utils/assetUrl';
import { resourceColorByCode } from '@/utils/elementColor';
import { useIsMobile } from '@/composables/useIsMobile';
import { useItemComparisonStore } from '@/stores/itemComparison.store';
import ItemTooltipContent from '@/components/items/ItemTooltipContent.vue';
import type { MeItemEntry } from '@/types/inventory';
import type { ExcavationLootItem } from '@/types/archaeology';
import {
  type DisplayItem,
  type DisplayStat,
  toDisplayItem,
  formatStat,
} from '@/types/itemDisplay';

const props = withDefaults(
  defineProps<{
    item: MeItemEntry | ExcavationLootItem | DisplayItem;
    size?: 'mini' | 'medium' | 'large';
    /** Affiche le tooltip riche au survol (mini/medium). */
    tooltip?: boolean;
    /** Affiche le bouton ⇄ d'épinglage pour la comparaison. */
    comparable?: boolean;
    placement?: Placement;
    /** Référence de comparaison EXPLICITE passée au tooltip (ex. composant équipé
     *  de la case du studio) ; le tooltip affiche les deltas par rapport à elle. */
    reference?: DisplayItem | null;
  }>(),
  {
    size: 'medium',
    tooltip: true,
    comparable: false,
    placement: 'top',
  },
);

const cmp = useItemComparisonStore();
// Mobile : pas de survol -> le tooltip riche s'ouvre au TAP (clic).
const isMobile = useIsMobile();

const display = computed<DisplayItem>(() => toDisplayItem(props.item));

const isRef = computed(() => cmp.isPinned(display.value.id));

function thumb(it: DisplayItem, size: 96 | 64 = 96): string | null {
  const code = it.imageCode ?? it.code;
  return code ? thumbUrlFromCode(it.imageFolder ?? 'items', code, size) : null;
}

/** 2 stats les plus pertinentes (primaire d'abord, déjà ordonné). */
const topStats = computed<DisplayStat[]>(() => display.value.stats.slice(0, 2));

function onCompare(e: Event): void {
  e.stopPropagation();
  e.preventDefault();
  cmp.toggle(display.value);
}

function onImgError(e: Event): void {
  (e.target as HTMLImageElement).style.display = 'none';
}
</script>

<template>
  <!-- Mode grand : carte complète inline (pas de popover). -->
  <ItemTooltipContent v-if="size === 'large'" :item="display" :reference="reference" />

  <!-- Mini / Medium : déclencheur + popover riche (désactivable). -->
  <AppInfoCard
    v-else
    :disabled="!tooltip"
    :width="330"
    :placement="placement"
    popper-class="item-tt-popover"
    :trigger="isMobile ? 'click' : 'hover'"
  >
    <div
      class="item-card"
      :class="[`size-${size}`, `rarity-${display.rarity}`, { 'is-ref': isRef, 'is-set': display.setId != null, 'has-languette': size === 'medium' && display.typeLabel, 'has-slotsize': size === 'medium' && display.slotSize }]"
    >
      <!-- Languette de TYPE (medium) : type vertical à gauche (Relique / Moteur…). -->
      <span v-if="size === 'medium' && display.typeLabel" class="item-languette">{{ display.typeLabel }}</span>

      <!-- Indication COMPOSANT (medium) : taille en hexagones colorés par la
           ressource, verticale à droite (taille + ressource en un coup). -->
      <span
        v-if="size === 'medium' && display.slotSize"
        class="item-slotsize"
        :title="`Taille ${display.slotSize}/4${display.fuelLabel ? ' · ' + display.fuelLabel : ''}`"
      >
        <span
          v-for="n in 4"
          :key="n"
          class="ssz-hex"
          :class="{ on: n <= (display.slotSize ?? 0) }"
          :style="n <= (display.slotSize ?? 0) ? { color: resourceColorByCode(display.fuelCode) } : undefined"
        >⬢</span>
      </span>

      <!-- Vignette + quantité -->
      <div class="item-thumb">
        <img
          v-if="thumb(display)"
          :src="thumb(display)!"
          :alt="display.name"
          class="item-thumb-img"
          loading="lazy"
          @error="onImgError"
        />
        <span v-if="display.quantity && display.quantity > 1" class="item-qty text-mono">
          ×{{ display.quantity }}
        </span>
        <!-- Étoiles de forge : niveau d'augmentation (atelier station). -->
        <span
          v-if="display.augmentationLevel && display.augmentationLevel > 0"
          class="item-aug"
          :title="`Augmentation +${display.augmentationLevel}`"
        >★{{ display.augmentationLevel }}</span>
        <!-- Badge « Basique » : composant de départ (type IsFixed, plancher de
             qualité) — le distingue des pièces lootées de même rareté Commun. -->
        <span
          v-if="display.isBasic"
          class="item-basic"
          title="Composant basique — équipement de départ (stats au plancher de qualité, non looté)"
        >🔧</span>
      </div>

      <!-- Corps (medium uniquement) -->
      <div v-if="size === 'medium'" class="item-body">
        <div class="item-head">
          <strong class="item-name">{{ display.name }}</strong>
        </div>
        <div v-if="topStats.length" class="item-stats">
          <span
            v-for="s in topStats"
            :key="s.key"
            class="item-stat"
            :class="{ 'is-neg': s.value < 0, 'is-pos': s.value > 0 }"
          >
            {{ s.label }} {{ formatStat(s) }}
          </span>
        </div>
        <div v-if="$slots.actions" class="item-actions">
          <slot name="actions" :item="display" />
        </div>
      </div>

      <!-- Bouton de comparaison (épingler comme référence) -->
      <button
        v-if="comparable"
        type="button"
        class="item-cmp-btn"
        :class="{ 'is-active': isRef }"
        :title="isRef ? 'Objet de référence — cliquer pour dépingler' : 'Comparer : épingler comme référence'"
        @click="onCompare"
      >⇄</button>
    </div>

    <template #content>
      <ItemTooltipContent :item="display" :reference="reference" />
    </template>
  </AppInfoCard>
</template>

<style lang="scss" scoped>
.item-card {
  position: relative;
  display: flex;
  gap: $space-3;
  background: $bg-tertiary;
  border: 1px solid $border-default;
  border-radius: $radius-md;
  transition: all $duration-fast $easing-default;

  &:hover {
    transform: translateY(-2px);
    border-color: $accent-primary;
    @include neon-glow($accent-primary);
  }

  // Rareté = CONTOUR complet coloré (langage visuel unique : la rareté ne
  // s'affiche plus jamais en texte, seulement par la couleur du cadre) + un
  // FOND légèrement teinté de la même couleur (dégradé discret depuis le haut,
  // pas trop voyant mais qui dénote la rareté d'un coup d'œil).
  // Échelle 0..3 = Commun / Rare / Épique / Légendaire.
  &.rarity-0 { border-color: $rarity-common; }
  &.rarity-1 {
    border-color: $rarity-rare;
    background: linear-gradient(160deg, rgba($rarity-rare, 0.12), rgba($rarity-rare, 0.02) 55%), $bg-tertiary;
  }
  &.rarity-2 {
    border-color: $rarity-epic;
    background: linear-gradient(160deg, rgba($rarity-epic, 0.14), rgba($rarity-epic, 0.03) 55%), $bg-tertiary;
  }
  &.rarity-3 {
    border-color: $rarity-legendary;
    background: linear-gradient(160deg, rgba($rarity-legendary, 0.16), rgba($rarity-legendary, 0.03) 55%), $bg-tertiary;
    box-shadow: 0 0 0 1px rgba($rarity-legendary, 0.3);
  }

  // Set de composants : contour + fond VERT prioritaires sur la rareté (définis
  // après les .rarity-* pour gagner à spécificité égale).
  &.is-set {
    border-color: $rarity-set;
    background: linear-gradient(160deg, rgba($rarity-set, 0.14), rgba($rarity-set, 0.03) 55%), $bg-tertiary;
    box-shadow: 0 0 0 1px rgba($rarity-set, 0.3);
  }

  // Objet épinglé comme référence de comparaison.
  &.is-ref {
    border-color: $accent-primary;
    box-shadow: 0 0 0 1px rgba($accent-primary, 0.45);
  }
}

// ---- Tailles --------------------------------------------------------------
.size-mini {
  padding: 0;
  width: 64px;
  height: 64px;

  .item-thumb { width: 64px; height: 64px; }
}

.size-medium {
  padding: $space-3;
  width: 100%;              // remplit la cellule (grille inventaire / marché) -> nom ellipsé
  box-sizing: border-box;

  // Place pour la languette de type (bande verticale gauche).
  &.has-languette { padding-left: calc(#{$space-3} + 22px); }
  // Place pour l'indication de taille composant (bande verticale droite).
  &.has-slotsize { padding-right: calc(#{$space-3} + 20px); }
}

// ---- Indication de taille composant (bande verticale droite) --------------
.item-slotsize {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  background: rgba(255, 255, 255, 0.04);
  border-left: 1px solid $border-default;
  border-radius: 0 $radius-md $radius-md 0;
  pointer-events: none;
}
.ssz-hex {
  font-size: 10px;
  line-height: 1;
  color: rgba(255, 255, 255, 0.18);
  &.on { color: $color-neon-cyan; }   // fallback si pas de ressource
}

// ---- Languette de type (medium) -------------------------------------------
.item-languette {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  writing-mode: vertical-rl;
  transform: rotate(180deg);          // lecture de bas en haut (-90°)
  white-space: nowrap;
  overflow: hidden;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: $text-secondary;
  background: rgba(255, 255, 255, 0.04);
  border-right: 1px solid $border-default;
  border-radius: $radius-md 0 0 $radius-md;
  pointer-events: none;
}
// Teinte de la languette par rareté (cohérent avec le contour).
.rarity-0 .item-languette { color: $rarity-common; }
.rarity-1 .item-languette { color: $rarity-rare; }
.rarity-2 .item-languette { color: $rarity-epic; }
.rarity-3 .item-languette { color: $rarity-legendary; }
.is-set .item-languette { color: $rarity-set; }

// ---- Vignette -------------------------------------------------------------
.item-thumb {
  position: relative;
  width: 64px;
  height: 64px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.03);
  border-radius: $radius-sm;
  overflow: hidden;
}

.item-thumb-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

// Badge « étoiles de forge » : niveau d'augmentation, coin haut-gauche de la vignette.
.item-aug {
  position: absolute;
  top: 2px;
  left: 2px;
  padding: 0 4px;
  font-size: 10px;
  font-weight: $font-weight-bold;
  line-height: 1.4;
  color: #1b1407;
  background: $rarity-legendary;
  border-radius: $radius-sm;
  pointer-events: none;
}

// Badge « Basique » : coin haut-droit de la vignette (l'étoile de forge occupe le
// coin haut-gauche). Puce sombre neutre : marque l'équipement de départ sans
// prétendre à une rareté.
.item-basic {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  line-height: 1;
  border-radius: $radius-sm;
  background: rgba(0, 0, 0, 0.7);
  border: 1px solid $border-default;
  pointer-events: none;
}

.item-qty {
  position: absolute;
  bottom: 2px;
  right: 4px;
  padding: 1px 5px;
  font-size: $font-size-xs;
  color: $text-primary;
  background: rgba(0, 0, 0, 0.75);
  border-radius: $radius-sm;
  pointer-events: none;
}

// ---- Corps (medium) -------------------------------------------------------
.item-body {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: $space-1;
  min-width: 0;
}

.item-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: $space-2;
}

.item-name {
  font-size: $font-size-sm;
  color: $text-primary;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.item-stat {
  font-size: $font-size-xs;
  font-family: $font-mono;
  padding: 1px 6px;
  border-radius: $radius-sm;
  background: rgba(255, 255, 255, 0.04);
  color: $text-secondary;

  &.is-pos { color: $color-success; }
  &.is-neg { color: $color-danger; }
}

.item-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 4px;
}

// ---- Bouton comparaison ---------------------------------------------------
.item-cmp-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  line-height: 1;
  color: $text-muted;
  background: rgba(0, 0, 0, 0.55);
  border: 1px solid $border-default;
  border-radius: $radius-sm;
  cursor: pointer;
  opacity: 0;
  transition: all $duration-fast $easing-default;

  .item-card:hover & { opacity: 1; }

  &:hover {
    color: $accent-primary;
    border-color: $accent-primary;
  }

  &.is-active {
    opacity: 1;
    color: $bg-primary;
    background: $accent-primary;
    border-color: $accent-primary;
  }
}
</style>
